/**
 * PATH: packages/email-hub/src/feature/reader-header/workflow/WatcherManager.tsx
 * FILE_ID: EMAILHUB-READERHEADER-WORKFLOW-WATCHERMGR-001
 * PURPOSE: Watcher Manager overlay — current watchers with remove, invite
 *   candidates (assignee excluded), and copy-list (plan US-23).
 * ROLE IN ARCHITECTURE: Presentational overlay leaf under WorkflowRow.
 * MAIN EXPORTS: WatcherManager.
 * NON-RESPONSIBILITIES: Mutations/busy state (useWorkflowCells), avatar menus
 *   (WorkflowRow routes them through the workflowWatchers scope).
 * NOTES FOR FUTURE AI: Geometry comes from AdaptiveOverlay (flip/clamp — the
 *   overlay never covers its trigger, plan popover-collision glitch kill).
 */
import type { RefObject } from 'react'
import { ClipboardCopy, UserMinus, UserPlus } from 'lucide-react'
import { AdaptiveOverlay } from '@quotech/ui'

import { AllocationAvatar } from '../../components/email-allocation'
import type { WorkflowWatchersViewModel } from './useWorkflowCells'

export interface WatcherManagerProps {
  vm: WorkflowWatchersViewModel
  anchorRef: RefObject<HTMLElement | null>
}

export function WatcherManager({ vm, anchorRef }: WatcherManagerProps) {
  return (
    <AdaptiveOverlay
      open={vm.showPicker}
      onOpenChange={vm.setShowPicker}
      anchorRef={anchorRef}
      label="Watcher Manager"
      mode="auto"
      popoverWidth={288}
      testId="reader-watcher-manager"
    >
      <div className="max-h-72 overflow-y-auto p-1">
        <div className="flex items-center justify-between px-2 pb-1 pt-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Watching ({vm.watcherList.length})
          </span>
          {vm.watcherList.length > 0 && (
            <button
              type="button"
              onClick={vm.copyList}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted pointer-coarse:min-h-11"
              data-testid="watcher-manager-copy-list"
            >
              <ClipboardCopy className="h-3 w-3" />
              Copy list
            </button>
          )}
        </div>
        {vm.watcherList.length === 0 && (
          <p className="px-2 py-1 text-[11px] italic text-muted-foreground">
            No watchers yet.
          </p>
        )}
        {vm.watcherList.map((watcher) => (
          <div
            key={watcher.user_id}
            className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
            data-testid={`watcher-manager-row-${watcher.user_id}`}
          >
            <AllocationAvatar
              size="xs"
              name={watcher.user_name}
              email={watcher.user_email}
              avatar_url={
                vm.members.find((m) => m.id === watcher.user_id)?.avatar_url
              }
            />
            <span className="min-w-0 flex-1 truncate text-[12px]">
              {watcher.user_name || watcher.user_email}
            </span>
            <button
              type="button"
              onClick={() => void vm.removeWatcher(watcher)}
              disabled={!!vm.busyKey}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:text-rose-400 dark:hover:bg-rose-950/40 pointer-coarse:min-h-11"
              data-testid={`watcher-manager-remove-${watcher.user_id}`}
            >
              <UserMinus className="h-3 w-3" />
              Remove
            </button>
          </div>
        ))}
        <div className="mt-1 border-t border-border/60 px-2 pb-1 pt-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Invite
          </span>
        </div>
        {vm.inviteCandidates.length === 0 && (
          <p className="px-2 py-1 text-[11px] text-muted-foreground">
            Everyone is already watching.
          </p>
        )}
        {vm.inviteCandidates.map((member) => (
          <button
            key={member.id}
            type="button"
            onClick={() => void vm.inviteWatcher(member)}
            disabled={!!vm.busyKey}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted disabled:opacity-50"
            data-testid={`watcher-manager-invite-${member.id}`}
          >
            <AllocationAvatar
              size="xs"
              name={member.name}
              email={member.email}
              avatar_url={member.avatar_url}
            />
            <span className="truncate">{member.name}</span>
            <UserPlus className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
          </button>
        ))}
      </div>
    </AdaptiveOverlay>
  )
}
